import { Box, Paper, Tab, Tabs } from "@mui/material";
import { memo, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import useUniqueTable from "app/hooks/useUniqueTable";
import { useTables } from "app/hooks/useTables";
import type { RecordEnvelope } from "modules/record/record-producer/hooks/useRecordProducerQuery";

import RelatedListPanel from "./RelatedListPanel";
import RelatedListSkeleton from "./RelatedListSkeleton";
import { useRelatedListDefinitions } from "../hooks/useRelatedListDefinitions";
import { useResolvedRelatedLists } from "../hooks/useResolvedRelatedLists";

const RELATED_LIST_DEBUG_STORAGE_KEY = "debug.relatedList";

const isRelatedListDebugEnabled = () => {
    if (typeof window === "undefined") {
        return false;
    }

    return window.localStorage.getItem(RELATED_LIST_DEBUG_STORAGE_KEY) === "1";
};

const RecordRelatedListComponent = ({ record }: { record?: RecordEnvelope | null }) => {
    const { t } = useTranslation();
    const { data: tableList = [] } = useTables();
    const { tableName: routeTableName, id: routeId } = useParams();
    const location = useLocation();
    const table = useUniqueTable(routeTableName || "");
    const [currentTab, setCurrentTab] = useState(0);

    const { relatedLists, isLoading, hasRelatedLists } = useRelatedListDefinitions(
        table,
        tableList.length > 0,
        routeTableName === "sys_tables" ? record?.data?.parent_table_name : table?.parent_table_name
    );
    const resolvedRelatedLists = useResolvedRelatedLists(relatedLists);
    const relatedListSignature = resolvedRelatedLists.map(list => `${list.definition.id}:${list.relationshipType}`).join("|");
    const relatedListDebugEnabled = isRelatedListDebugEnabled();

    useEffect(() => {
        const requestedList = new URLSearchParams(location.search).get("related_list");
        const requestedIndex = requestedList === "inheritance_ancestors"
            ? resolvedRelatedLists.findIndex(list => list.relationshipType === "inheritance_ancestors") : -1;
        setCurrentTab(requestedIndex >= 0 ? requestedIndex : 0);
    }, [routeId, routeTableName, table?.name, location.search, relatedListSignature]);

    useEffect(() => {
        if (currentTab < resolvedRelatedLists.length) {
            return;
        }

        setCurrentTab(0);
    }, [currentTab, resolvedRelatedLists.length]);

    const handleTabChange = (_: SyntheticEvent, nextTab: number) => {
        setCurrentTab(nextTab);
    };

    const tabsContent = useMemo(() => (
        <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
                maxWidth: "100%",
                minWidth: 0,
                "& .MuiTabs-scroller": {
                    overflowX: "auto !important",
                    WebkitOverflowScrolling: "touch",
                },
                "& .MuiTabs-flexContainer": {
                    minWidth: "max-content",
                },
            }}
        >
            {resolvedRelatedLists.map((relatedList, index) => (
                <Tab
                    key={`${relatedList.definition.id}_${index}_related_list_tab`}
                    value={index}
                    label={t(relatedList.definition.name)}
                    sx={{ flexShrink: 0 }}
                />
            ))}
        </Tabs>
    ), [currentTab, resolvedRelatedLists, t]);

    if (isLoading) {
        return <RelatedListSkeleton />;
    }

    if (!hasRelatedLists) {
        return null;
    }

    const activeRelatedList = resolvedRelatedLists[currentTab];

    if (relatedListDebugEnabled) {
        console.log(`[RelatedListDebugJSON] container ${JSON.stringify({
            routeTableName: routeTableName || null,
            routeId: routeId || null,
            currentTable: table?.name || null,
            currentTab,
            resolvedRelatedLists: resolvedRelatedLists.map((relatedList) => ({
                definitionId: relatedList.definition.id,
                definitionName: relatedList.definition.name,
                tableName: relatedList.tableName,
                relationshipType: relatedList.relationshipType,
                targetColumnName: relatedList.targetColumnName,
            })),
            activeRelatedList: activeRelatedList
                ? {
                    definitionId: activeRelatedList.definition.id,
                    definitionName: activeRelatedList.definition.name,
                    tableName: activeRelatedList.tableName,
                    relationshipType: activeRelatedList.relationshipType,
                    targetColumnName: activeRelatedList.targetColumnName,
                }
                : null,
        })}`);
    }

    return (
        <Paper key={`${routeTableName}_${routeId}_related_list`} elevation={1} sx={{ marginTop: 2 }}>
            <Box sx={{ px: 1, pt: 0.5, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                {tabsContent}
            </Box>
            {activeRelatedList && (
                <Box>
                    <RelatedListPanel
                        key={`${activeRelatedList.definition.id}_${currentTab}_related_list`}
                        relatedList={activeRelatedList}
                        routeId={routeId || ""}
                    />
                </Box>
            )}
        </Paper>
    );
};

export default memo(RecordRelatedListComponent);
